import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Percent,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Moon,
  Sun,
  Calendar,
  Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const SURCHARGE_ICONS = {
  night: Moon,
  saturday: Calendar,
  sunday: Sun,
  holiday: Calendar,
};

const SURCHARGE_COLORS = {
  night: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  saturday: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  sunday: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  holiday: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function SurchargeRulesPanel({ 
  customerId, 
  projectId = null, 
  projectCustomerId = null,
  isProjectLevel = false 
}) {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [effectiveRules, setEffectiveRules] = useState([]);
  const [surchargeTypes, setSurchargeTypes] = useState({});
  const [overrideMode, setOverrideMode] = useState('inherit');
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  const [formData, setFormData] = useState({
    surcharge_type: 'night',
    percent: 25,
    label: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, [customerId, projectId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [typesRes] = await Promise.all([
        api.get('/billing/surcharges/types'),
      ]);
      setSurchargeTypes(typesRes.data);

      if (isProjectLevel && projectId) {
        const rulesRes = await api.get(`/billing/surcharges/project/${projectId}`);
        setRules(rulesRes.data.project_rules || []);
        setEffectiveRules(rulesRes.data.effective_rules || []);
        setOverrideMode(rulesRes.data.override_mode || 'inherit');
      } else if (customerId) {
        const rulesRes = await api.get(`/billing/surcharges/customer/${customerId}`);
        setRules(rulesRes.data);
        setEffectiveRules(rulesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch surcharge rules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRule(null);
    const availableTypes = Object.keys(surchargeTypes).filter(
      t => !rules.some(r => r.surcharge_type === t)
    );
    const defaultType = availableTypes[0] || 'night';
    
    setFormData({
      surcharge_type: defaultType,
      percent: surchargeTypes[defaultType]?.default_percent || 25,
      label: surchargeTypes[defaultType]?.label || '',
      is_active: true,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      surcharge_type: rule.surcharge_type,
      percent: rule.percent,
      label: rule.label || '',
      is_active: rule.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const endpoint = isProjectLevel 
        ? `/billing/surcharges/project/${projectId}`
        : `/billing/surcharges/customer/${customerId}`;
      
      if (editingRule) {
        await api.put(`${endpoint}/${editingRule.id}`, {
          percent: formData.percent,
          label: formData.label || null,
          is_active: formData.is_active,
        });
        toast.success('Zuschlagsregel aktualisiert');
      } else {
        await api.post(endpoint, formData);
        toast.success('Zuschlagsregel erstellt');
      }
      
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Möchten Sie diese Zuschlagsregel wirklich löschen?')) return;
    
    try {
      const endpoint = isProjectLevel 
        ? `/billing/surcharges/project/${projectId}/${ruleId}`
        : `/billing/surcharges/customer/${customerId}/${ruleId}`;
      
      await api.delete(endpoint);
      toast.success('Zuschlagsregel gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleSetupDefaults = async () => {
    if (!customerId) return;
    
    try {
      await api.post(`/billing/surcharges/customer/${customerId}/setup-defaults`);
      toast.success('Standardregeln erstellt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const endpoint = isProjectLevel 
        ? `/billing/surcharges/project/${projectId}/${rule.id}`
        : `/billing/surcharges/customer/${customerId}/${rule.id}`;
      
      await api.put(endpoint, { is_active: !rule.is_active });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleOverrideModeChange = async (mode) => {
    if (!projectId) return;
    
    try {
      await api.put(`/billing/surcharges/project/${projectId}/override-mode`, {
        override_mode: mode,
      });
      setOverrideMode(mode);
      toast.success(`Override-Modus auf '${mode}' gesetzt`);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Ändern des Override-Modus');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-lg font-medium text-foreground">
            {isProjectLevel ? 'Projekt-Zuschläge' : 'Zuschlagsregeln'}
          </h3>
        </div>
        <div className="flex gap-2">
          {!isProjectLevel && rules.length === 0 && (
            <Button
              type="button"
              onClick={handleSetupDefaults}
              variant="outline"
              size="sm"
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Standardregeln
            </Button>
          )}
          <Button
            type="button"
            onClick={openCreateDialog}
            size="sm"
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            Hinzufügen
          </Button>
        </div>
      </div>

      {/* Override Mode for Projects */}
      {isProjectLevel && (
        <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
          <span className="text-sm text-muted-foreground">Kunden-Zuschläge:</span>
          <Select value={overrideMode} onValueChange={handleOverrideModeChange}>
            <SelectTrigger className="w-40 h-8 bg-input border-border text-foreground text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="inherit">Übernehmen</SelectItem>
              <SelectItem value="override">Überschreiben</SelectItem>
              <SelectItem value="replace">Ignorieren</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Rules Table */}
      {rules.length > 0 || (isProjectLevel && effectiveRules.length > 0) ? (
        <div className="space-y-2">
          {(isProjectLevel ? effectiveRules : rules).map((rule) => {
            const Icon = SURCHARGE_ICONS[rule.surcharge_type] || Clock;
            const colorClass = SURCHARGE_COLORS[rule.surcharge_type] || 'bg-muted text-muted-foreground border-border/30';
            const isInherited = isProjectLevel && !rules.find(r => r.id === rule.id);
            
            return (
              <div
                key={rule.id}
                className={`flex items-center justify-between p-3 rounded-lg bg-muted/50 ${!rule.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass.split(' ')[0]}`}>
                    <Icon className={`w-4 h-4 ${colorClass.split(' ')[1]}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">{rule.label}</span>
                      <span className={`px-2 py-0.5 rounded text-xs border ${colorClass}`}>
                        +{rule.percent}%
                      </span>
                      {isInherited && (
                        <span className="px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground">
                          vom Kunden
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{rule.surcharge_type}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!isInherited && (
                    <>
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggleRule(rule)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(rule)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(rule.id)}
                        className="text-muted-foreground hover:text-red-600 dark:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground">
          <Percent className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Keine Zuschlagsregeln definiert</p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingRule ? 'Zuschlag bearbeiten' : 'Neuer Zuschlag'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Zuschlagsart</Label>
              <Select
                value={formData.surcharge_type}
                onValueChange={(v) => setFormData({ 
                  ...formData, 
                  surcharge_type: v,
                  label: surchargeTypes[v]?.label || '',
                  percent: surchargeTypes[v]?.default_percent || 25,
                })}
                disabled={!!editingRule}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {Object.entries(surchargeTypes).map(([type, info]) => (
                    <SelectItem key={type} value={type}>
                      {info.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Zuschlag (%)</Label>
              <Input
                type="number"
                value={formData.percent}
                onChange={(e) => setFormData({ ...formData, percent: parseFloat(e.target.value) || 0 })}
                min="0"
                max="500"
                step="5"
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Label (optional)</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder={surchargeTypes[formData.surcharge_type]?.label || ''}
                className="bg-input border-border text-foreground"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-border text-muted-foreground"
              >
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                {editingRule ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
